import * as React from "react";
import { BaseModal } from "../../atoms/base-modal";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { PublishStatus, PublishSetting } from "@graffer-inc/cloud-gov-core";
import dayjs from "../../../utils/date-utils";
import {
  RadioButton,
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";

interface Props {
  publishStatus: PublishStatus;
  publishSetting: PublishSetting;
  isPublished: boolean;
  isActive: boolean;
  onPublishSettingUpdate: (
    setting: PublishSetting,
    withPublish: boolean,
    isPublishNow: boolean,
    isStartAcceptAtPublish: boolean,
  ) => void;
  onClose: () => void;
}

export const PublishSettingModal: React.FunctionComponent<Props> = ({
  publishSetting,
  publishStatus,
  isPublished,
  isActive,
  onPublishSettingUpdate,
  onClose,
}) => {
  const [setting, setSetting] = useState<PublishSetting>(publishSetting);
  const [startPublishType, setStartPublishType] = useState<string>("NOW");
  const [startAcceptType, setStartAcceptType] = useState<string>(
    "SAME_START_PUBLISH_AT",
  );
  const [endAcceptType, setEndAcceptType] = useState<string>("NOT_SET");
  const [endPublishType, setEndPublishType] = useState<string>("NOT_SET");

  useEffect(() => {
    setSetting(publishSetting);
    if (publishSetting.publishAt) {
      setStartPublishType("SPECIFY");
    }
    if (
      isPublished &&
      !!publishSetting.startAcceptAt &&
      publishSetting.startAcceptAt !== publishSetting.publishAt
    ) {
      setStartAcceptType("SPECIFY");
    }
    if (publishSetting.endAcceptAt) {
      setEndAcceptType("SPECIFY");
    }
    if (publishSetting.unPublishAt) {
      setEndPublishType("SPECIFY");
    }
  }, [publishSetting, publishStatus, isPublished]);

  const isValidStartPublishAt = () => {
    if (startPublishType === "SPECIFY") {
      if (!setting.publishAt) {
        return false;
      }
      if (isPublished) {
        return true;
      }

      const now = dayjs().startOf("minute");
      return !dayjs(setting.publishAt).isBefore(now);
    }
    return true;
  };

  const isValidStartAcceptAt = () => {
    if (startAcceptType === "SPECIFY") {
      if (!setting.startAcceptAt) {
        return false;
      }
      if (startPublishType === "SPECIFY" && setting.publishAt) {
        const startAcceptAt = dayjs(setting.startAcceptAt);
        const publishAt = dayjs(setting.publishAt);

        return (
          startAcceptAt.isSame(publishAt) || startAcceptAt.isAfter(publishAt)
        );
      } else {
        if (isPublished) {
          return true;
        }

        const now = dayjs().startOf("minute");
        const startAcceptAt = dayjs(setting.startAcceptAt);
        return !startAcceptAt.isBefore(now);
      }
    }
    return true;
  };

  const isValidEndAcceptAt = () => {
    if (endAcceptType === "SPECIFY") {
      if (!setting.endAcceptAt) {
        return false;
      }

      const endAcceptAt = dayjs(setting.endAcceptAt);
      if (startAcceptType === "SPECIFY" && setting.startAcceptAt) {
        const startAcceptAt = dayjs(setting.startAcceptAt);
        return (
          endAcceptAt.isSame(startAcceptAt) ||
          endAcceptAt.isAfter(startAcceptAt)
        );
      } else if (startPublishType === "SPECIFY" && setting.publishAt) {
        const publishAt = dayjs(setting.publishAt);
        return endAcceptAt.isSame(publishAt) || endAcceptAt.isAfter(publishAt);
      } else {
        if (isPublished) {
          return true;
        }

        const now = dayjs().startOf("minute");
        return !endAcceptAt.isBefore(now);
      }
    }
    return true;
  };

  const isValidEndPublishAt = () => {
    if (endPublishType === "SPECIFY") {
      if (!setting.unPublishAt) {
        return false;
      }

      const unPublishAt = dayjs(setting.unPublishAt);
      if (endAcceptType === "SPECIFY" && setting.endAcceptAt) {
        const endAcceptAt = dayjs(setting.endAcceptAt);
        return (
          unPublishAt.isSame(endAcceptAt) || unPublishAt.isAfter(endAcceptAt)
        );
      } else if (startAcceptType === "SPECIFY" && setting.startAcceptAt) {
        const startedAcceptAt = dayjs(setting.startAcceptAt);
        return (
          unPublishAt.isSame(startedAcceptAt) ||
          unPublishAt.isAfter(startedAcceptAt)
        );
      } else if (startPublishType === "SPECIFY" && setting.publishAt) {
        const publishAt = dayjs(setting.publishAt);
        return unPublishAt.isSame(publishAt) || unPublishAt.isAfter(publishAt);
      } else {
        if (isPublished) {
          return true;
        }

        const now = dayjs().startOf("minute");
        return !unPublishAt.isBefore(now);
      }
    }
    return true;
  };

  const canUpdateStartPublishAt = () => {
    return ["DRAFT", "BEFORE_PUBLISH"].includes(publishStatus);
  };

  const canUpdateStartAcceptAt = () => {
    return publishStatus !== "ACCEPTING";
  };

  return (
    <BaseModal
      title={"公開・受付日時の設定"}
      isActive={isActive}
      onClose={onClose}
    >
      <section className="modal-card-body">
        {!isPublished && (
          <article className="message is-warning">
            <div className="message-body">
              設定を確定すると、設定内容に基づき公開・受付が開始されます。また、
              <b>
                公開開始日時を過ぎると、フォームなどの更新ができなくなります。
              </b>
              設定内容をご確認の上、設定を確定させてください。
            </div>
          </article>
        )}
        {publishStatus === "PAUSING_ACCEPT" && (
          <article className="message is-warning">
            <div className="message-body">
              設定を更新すると、設定内容に基づき受付が再開されます。
            </div>
          </article>
        )}
        <div className="field">
          <div className="control" style={{ padding: "0 0 10px 0" }}>
            <RadioButton
              description=""
              label="公開開始日時"
              onChange={(e) => setStartPublishType(e.target.value)}
              options={[
                { name: "日時を指定する", value: "SPECIFY" },
                { name: "現在日時を設定する", value: "NOW" },
              ]}
              value={startPublishType}
              direction="row"
              disabled={!canUpdateStartPublishAt()}
            />
          </div>
          {startPublishType === "SPECIFY" && (
            <div className="control">
              <ReactDatePicker
                className="input"
                disabled={!canUpdateStartPublishAt()}
                selected={
                  // publishAtのタイムゾーンがUTCなので、JSTのDateオブジェクトに変換する必要がある
                  setting.publishAt ? dayjs(setting.publishAt).toDate() : null
                }
                onChange={(date) => {
                  setSetting({
                    ...setting,
                    publishAt: date as Date,
                  });
                }}
                placeholderText="----/--/-- --:--"
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={1}
                timeCaption="時分"
                dateFormat="yyyy/MM/dd HH:mm"
              />
              {!isValidStartPublishAt() && (
                <p className="is-size-7 has-text-danger">
                  現在日時より先の日時を入力してください
                </p>
              )}
            </div>
          )}
        </div>
        <div className="field">
          <div className="control">
            <RadioButton
              description=""
              label="受付開始日時"
              onChange={(e) => setStartAcceptType(e.target.value)}
              options={[
                { name: "日時を指定する", value: "SPECIFY" },
                {
                  name: "公開開始日時と同じにする",
                  value: "SAME_START_PUBLISH_AT",
                },
              ]}
              value={startAcceptType}
              direction="row"
              disabled={!canUpdateStartAcceptAt()}
            />
          </div>
          {startAcceptType === "SPECIFY" && (
            <div className="control" style={{ padding: "10px 0 0 0" }}>
              <ReactDatePicker
                className="input"
                disabled={!canUpdateStartAcceptAt()}
                selected={
                  // startAcceptAtのタイムゾーンがUTCなので、JSTのDateオブジェクトに変換する必要がある
                  setting.startAcceptAt
                    ? dayjs(setting.startAcceptAt).toDate()
                    : null
                }
                placeholderText="----/--/-- --:--"
                onChange={(date) => {
                  setSetting({
                    ...setting,
                    startAcceptAt: date as Date,
                  });
                }}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={1}
                timeCaption="時分"
                dateFormat="yyyy/MM/dd HH:mm"
              />
              {!isValidStartAcceptAt() && (
                <p className="is-size-7 has-text-danger">
                  公開開始日時より先の日時を入力してください
                </p>
              )}
            </div>
          )}
        </div>

        <div className="field">
          <div className="control">
            <RadioButton
              description=""
              label="受付終了日時"
              onChange={(e) => setEndAcceptType(e.target.value)}
              options={[
                { name: "設定する", value: "SPECIFY" },
                { name: "設定しない", value: "NOT_SET" },
              ]}
              value={endAcceptType}
              direction="row"
            />
          </div>
          {endAcceptType === "SPECIFY" && (
            <div className="control" style={{ padding: "10px 0 0 0" }}>
              <ReactDatePicker
                className="input"
                selected={
                  // endAcceptAtのタイムゾーンがUTCなので、JSTのDateオブジェクトに変換する必要がある
                  setting.endAcceptAt
                    ? dayjs(setting.endAcceptAt).toDate()
                    : null
                }
                onChange={(date) => {
                  setSetting({
                    ...setting,
                    endAcceptAt: date as Date,
                  });
                }}
                placeholderText="----/--/-- --:--"
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={1}
                timeCaption="時分"
                dateFormat="yyyy/MM/dd HH:mm"
              />
              {!isValidEndAcceptAt() && (
                <p className="is-size-7 has-text-danger">
                  受付開始日時より先の日時を入力してください
                </p>
              )}
            </div>
          )}
        </div>

        <div className="field">
          <div className="control">
            <RadioButton
              description=""
              label="公開終了日時"
              onChange={(e) => setEndPublishType(e.target.value)}
              options={[
                { name: "設定する", value: "SPECIFY" },
                { name: "設定しない", value: "NOT_SET" },
              ]}
              value={endPublishType}
              direction="row"
            />
          </div>
          {endPublishType === "SPECIFY" && (
            <div className="control" style={{ padding: "10px 0 0 0" }}>
              <ReactDatePicker
                className="input"
                selected={
                  // unPublishAtのタイムゾーンがUTCなので、JSTのDateオブジェクトに変換する必要がある
                  setting.unPublishAt
                    ? dayjs(setting.unPublishAt).toDate()
                    : null
                }
                onChange={(date) => {
                  setSetting({
                    ...setting,
                    unPublishAt: date as Date,
                  });
                }}
                placeholderText="----/--/-- --:--"
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={1}
                timeCaption="時分"
                dateFormat="yyyy/MM/dd HH:mm"
              />
              {!isValidEndPublishAt() && (
                <p className="is-size-7 has-text-danger">
                  {endAcceptType === "SPECIFY"
                    ? "受付終了日時"
                    : "受付開始日時"}
                  より先の日時を入力してください
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <footer className="modal-card-foot is-justify-content-center">
        <SecondaryButton text={"キャンセル"} onClick={onClose} />
        <PrimaryButton
          text={isPublished ? "更新する" : "設定を確定する"}
          disabled={
            !isValidStartPublishAt() ||
            !isValidStartAcceptAt() ||
            !isValidEndAcceptAt() ||
            !isValidEndPublishAt()
          }
          onClick={() => {
            const publishAt =
              startPublishType === "SPECIFY" ? setting.publishAt : undefined;
            const startAcceptAt =
              startAcceptType === "SPECIFY" ? setting.startAcceptAt : undefined;
            const endAcceptAt =
              endAcceptType === "SPECIFY" ? setting.endAcceptAt : undefined;
            const unPublishAt =
              endPublishType === "SPECIFY" ? setting.unPublishAt : undefined;
            const newSetting: PublishSetting = {
              isPausingAccept: false,
              publishAt,
              startAcceptAt,
              endAcceptAt,
              unPublishAt,
            };
            onPublishSettingUpdate(
              newSetting,
              !isPublished,
              startPublishType === "NOW",
              startAcceptType === "SAME_START_PUBLISH_AT",
            );
            onClose();
          }}
        />
      </footer>
    </BaseModal>
  );
};
